import React, { useRef } from "react";
import styles from "./QuestionCard.module.css";
import { CheckIcon } from "../icons/icons";

/* Single selectable answer (acts as a radio). */
const Option = ({ label, selected, onSelect, name }) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    name={name}
    className={`${styles.option} ${selected ? styles.optionSelected : ""}`}
    onClick={onSelect}
  >
    <span className={styles.optionMark}>
      <CheckIcon />
    </span>
    <span>{label}</span>
  </button>
);

/* Renders one quiz question. Supports both flat `options` questions and the
   grouped (multi-part) question via `groups`. Answers are reported up:
   - flat:    onChange(questionId, option)
   - grouped: onChange(questionId, option, groupIndex)
   `value` is either an option object, or a map { [groupIndex]: option }. */
const QuestionCard = ({ question, number, value, onChange }) => {
  const isGrouped = Array.isArray(question.groups);
  const groupRefs = useRef({});

  // Bring an element into view only when it isn't already fully visible.
  const revealIfNeeded = (el) => {
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (!(r.top >= 0 && r.bottom <= vh)) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // For the grouped question: after answering a sub-question, slide the next
  // sub-question into view (helps on mobile where they stack vertically).
  const handleGroupSelect = (opt, gi) => {
    onChange(question.id, opt, gi);
    const next = groupRefs.current[gi + 1];
    if (next) window.requestAnimationFrame(() => revealIfNeeded(next));
  };

  // The anona display font lacks a proper "%" glyph, so render it with the
  // mokoko fallback while keeping the rest of the text in anona.
  const renderText = (text) =>
    String(text)
      .split(/(%)/g)
      .map((part, i) =>
        part === "%" ? (
          <span key={i} className={styles.altGlyph}>
            %
          </span>
        ) : (
          part
        )
      );

  return (
    <div className={styles.qCard}>
      <div className={styles.qHead}>
        <span className={styles.qBadge}>{number}</span>
        <div className={styles.qTitleBlock}>
          <span className={styles.qKicker}>{question.title}</span>
          <p className={styles.qText}>{renderText(question.content)}</p>
        </div>
      </div>

      {isGrouped ? (
        question.groups.map((group, gi) => (
          <div key={gi} ref={(el) => (groupRefs.current[gi] = el)}>
            <p className={styles.groupLabel}>{group.label}</p>
            <div className={styles.options} role="radiogroup" aria-label={group.label}>
              {group.options.map((opt) => (
                <Option
                  key={opt.label}
                  label={opt.label}
                  name={`q${question.id}-g${gi}`}
                  selected={value && value[gi] && value[gi].label === opt.label}
                  onSelect={() => handleGroupSelect(opt, gi)}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className={styles.options} role="radiogroup" aria-label={question.content}>
          {question.options.map((opt) => (
            <Option
              key={opt.label}
              label={opt.label}
              name={`q${question.id}`}
              selected={value && value.label === opt.label}
              onSelect={() => onChange(question.id, opt)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
